import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/apiClient";
import HERO_BG from "@/lib/heroBg";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/archive");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-73px)] grid grid-cols-1 lg:grid-cols-2">
      <div
        className="hidden lg:block relative bg-center bg-cover"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      >
        <div className="absolute inset-0 bg-ink/60" />
        <div className="absolute bottom-0 p-12">
          <div className="font-mono text-xs tracking-[0.3em] uppercase text-parchment/70 mb-3">
            Members' Reading Room
          </div>
          <p className="font-serif text-4xl text-parchment leading-tight max-w-md">
            Return to the ledger and continue the discussion.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <h1 className="font-serif text-4xl text-ink tracking-tight mb-2">Sign In</h1>
          <p className="font-body text-text-muted mb-10">Enter your credentials to open the archive.</p>

          <form onSubmit={submit} className="space-y-6">
            {error && (
              <div
                data-testid="login-error"
                className="border border-oxblood bg-oxblood/5 text-oxblood px-4 py-3 font-body text-sm"
              >
                {error}
              </div>
            )}
            <div>
              <label className="block font-mono text-xs tracking-widest uppercase text-text-muted mb-2">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full bg-surface border border-archive-border px-4 py-3 font-mono text-sm text-ink focus:outline-none focus:ring-2 focus:ring-oxblood"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block font-mono text-xs tracking-widest uppercase text-text-muted mb-2">
                Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full bg-surface border border-archive-border px-4 py-3 font-mono text-sm text-ink focus:outline-none focus:ring-2 focus:ring-oxblood"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="w-full bg-oxblood text-parchment py-4 font-mono text-xs tracking-widest uppercase hover:bg-oxblood-dark transition-colors disabled:opacity-60"
            >
              {busy ? "Opening…" : "Sign In"}
            </button>
          </form>

          <p className="mt-8 font-body text-sm text-text-muted">
            Not yet a member?{" "}
            <Link to="/register" data-testid="to-register-link" className="text-oxblood link-underline pb-0.5">
              Request a seat at the table
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
